import { useAuth } from "../../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { Menu, Search, MapPin } from "lucide-react";
import api from "../../lib/api";
import { fmtPeriod } from "../../lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import ChangePasswordDialog from "./ChangePasswordDialog";
import { SidebarContent } from "../nav/Sidebar";
import { QuickSearch } from "../nav/QuickSearch";
import { useNavLocations, useNavLocation, usePins } from "../nav/useNavPrefs";
import { allItemsFor, hrefFor } from "../nav/navConfig";
import { OnboardingTour } from "../nav/OnboardingTour";

function greetingName(user) {
  const n = user?.name || user?.email || "";
  return n.split(" ")[0] || n;
}

export default function Header() {
  const { user, logout, authDisabled } = useAuth();
  const { pathname } = useLocation();
  const [freshness, setFreshness] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const locations = useNavLocations(user);
  const [location, setStoredLocation] = useNavLocation(locations);
  const navigate = useNavigate();
  const { search } = useLocation();
  // Switching location while on a location-aware page re-opens that page filtered to the new location.
  const setLocation = (loc) => {
    setStoredLocation(loc);
    const item = allItemsFor(user?.role).find((i) => i.loc && i.to === pathname);
    if (item && (loc || new URLSearchParams(search).has("location"))) navigate(hrefFor(item, loc), { replace: true });
  };
  const [pins, togglePin] = usePins(user);

  const loadFreshness = useCallback(
    () => api.get("/meta/freshness").then(({ data }) => setFreshness(data.latest_period)).catch(() => {}),
    [],
  );
  useEffect(() => {
    loadFreshness();
    window.addEventListener("data-updated", loadFreshness);
    return () => window.removeEventListener("data-updated", loadFreshness);
  }, [pathname, loadFreshness]);
  useEffect(() => {
    document.body.classList.add("has-sidebar");
    return () => document.body.classList.remove("has-sidebar");
  }, []);
  useEffect(() => setMobileOpen(false), [pathname]);

  const sidebarProps = {
    user, locations, location, setLocation, pins, togglePin, authDisabled,
    onSearch: () => { setMobileOpen(false); setSearchOpen(true); },
    onPassword: () => { setMobileOpen(false); setPwOpen(true); },
    onLogout: logout,
  };

  return (
    <>
      <aside className="hidden lg:block fixed left-0 top-0 bottom-0 w-72 z-40 bg-brand-page border-r border-brand-border" data-testid="side-menu">
        <SidebarContent {...sidebarProps} />
      </aside>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-80 bg-brand-page border-brand-border p-0" data-testid="side-menu-mobile">
          <SheetHeader className="sr-only"><SheetTitle>Menu</SheetTitle><SheetDescription>Main navigation</SheetDescription></SheetHeader>
          <SidebarContent {...sidebarProps} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>
      <QuickSearch open={searchOpen} setOpen={setSearchOpen} role={user?.role} locations={locations} location={location} setLocation={setLocation} />
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      {!authDisabled && <OnboardingTour user={user} hasLocations={locations.length > 0} />}

      <header className="sticky top-0 z-30 bg-brand-page/80 backdrop-blur-xl border-b border-brand-border">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => setMobileOpen(true)} data-testid="menu-trigger" aria-label="Open menu"
              className="lg:hidden p-2 -ml-2 text-brand-ink hover:bg-brand-hover rounded-lg transition-colors duration-200">
              <Menu className="h-5 w-5" />
            </button>
            <span className="lg:hidden font-serif text-xl tracking-tight text-brand-ink truncate">Bond Collective</span>
            {location && (
              <span data-testid="header-location-badge" className="hidden sm:inline-flex items-center gap-1 text-xs text-brand-ink bg-brand-surface border border-brand-border rounded-full px-3 py-1">
                <MapPin className="h-3 w-3 text-brand-tertiary" /> {location}
              </span>
            )}
            {freshness && (
              <span data-testid="data-freshness" className="hidden md:inline text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">
                Data as of {fmtPeriod(freshness)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => setSearchOpen(true)} data-testid="header-search" aria-label="Search"
              className="p-2 text-brand-muted hover:text-brand-ink hover:bg-brand-hover rounded-lg transition-colors lg:hidden"><Search className="h-4 w-4" /></button>
            <div className="text-right leading-tight" data-testid="user-greeting">
              <span className="text-sm text-brand-ink">Hello, {greetingName(user)}</span>
              {authDisabled && <span className="block text-[10px] uppercase tracking-[0.15em] text-brand-gold">Preview · login off</span>}
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
